// ---------------------------------------------------------------------------
// LayerTree — Outline panel: app > pages > blocks > elements.
//
// Shows a summary bar, page tree with context menus, DnD block sorting,
// and recursive element tree navigation.
//
// All DETAIL EDITING happens in the page work panel (PropertyInspector).
// All ELEMENT INSERTION is done via the InsertPanel tab.
// ---------------------------------------------------------------------------

import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useStudioStore } from '@/store/studio-store'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import {
  Box,
  GripVertical,
  ChevronRight,
  FileText,
  Search,
  Plus,
  MoreHorizontal,
  Copy,
  Trash2,
  Pencil,
  Square,
  Type,
  Heading,
  Image,
  Star,
  Tag,
  MousePointerClick,
  TextCursorInput,
  AlignLeft,
  CheckSquare,
  ChevronsUpDown,
  List,
  Table,
  Minus,
  Columns2,
  Grid2x2,
  CreditCard,
  Link2,
  Eye,
  EyeOff,
  Scissors,
  WrapText,
  Clipboard,
  ArrowUp,
  ArrowDown,
  ChevronsDownUp,
  UnfoldVertical,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { CogoElement } from '@/features/page-editor/store/types'
import { findParentInTree } from '@/features/page-editor/store/canvas-slice'
import { isContainer } from '@/features/page-editor/lib/element-factory'
import { SHORTCUT } from '@/lib/shortcuts'
import { toast } from '@/store/toast-store'
import { getLibraryPanelCopy } from './library-panel-copy'
import { useStudioLocale } from '@/i18n'

// ---------------------------------------------------------------------------
// LayerTree — Main component
// ---------------------------------------------------------------------------

export function LayerTree() {
  const { locale } = useStudioLocale()
  const copy = getLibraryPanelCopy(locale)
  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)
  const blocks = useEditorStore((s) => s.blocks)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const selectionTier = useEditorStore((s) => s.selectionTier)
  const setActiveRail = useEditorStore((s) => s.setActiveRail)

  const totalBlocks = useMemo(() => Object.keys(blocks).length, [blocks])

  // Page inline rename state
  const [renamingPageId, setRenamingPageId] = useState<string | null>(null)
  const [pageRenameDraft, setPageRenameDraft] = useState('')
  const pageRenameRef = useRef<HTMLInputElement>(null)

  const startPageRename = useCallback((pageId: string) => {
    setRenamingPageId(pageId)
    setPageRenameDraft(pageId)
    requestAnimationFrame(() => pageRenameRef.current?.select())
  }, [])

  const commitPageRename = useCallback(() => {
    if (!renamingPageId) return
    const trimmed = pageRenameDraft.trim()
    if (trimmed && trimmed !== renamingPageId) {
      useEditorStore.getState().renamePage(renamingPageId, trimmed)
      useEditorStore.getState().selectPage(trimmed)
    }
    setRenamingPageId(null)
  }, [renamingPageId, pageRenameDraft])

  // Collapse all: increment key to force remount, set default expanded
  const [collapseKey, setCollapseKey] = useState(0)
  const [defaultExpanded, setDefaultExpanded] = useState<boolean | null>(null)

  const collapseAll = useCallback(() => {
    setDefaultExpanded(false)
    setCollapseKey((k) => k + 1)
  }, [])

  const expandAll = useCallback(() => {
    setDefaultExpanded(true)
    setCollapseKey((k) => k + 1)
  }, [])

  // Controlled page collapse: only active page open by default
  const [openPages, setOpenPages] = useState<Set<string>>(
    () => new Set(activePageId ? [activePageId] : []),
  )

  // Auto-open active page and close others when page changes
  useEffect(() => {
    if (activePageId) {
      setOpenPages(new Set([activePageId]))
    }
  }, [activePageId])

  const togglePage = useCallback((pageId: string, isOpen: boolean) => {
    setOpenPages((prev) => {
      const next = new Set(prev)
      if (isOpen) next.add(pageId)
      else next.delete(pageId)
      return next
    })
  }, [])

  if (!app) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-sm text-muted-foreground text-center">
          {copy.noTemplateLoaded}
        </p>
      </div>
    )
  }

  const handlePageClick = (pageId: string) => {
    useEditorStore.getState().setActivePage(pageId)
    useEditorStore.getState().selectPage(pageId)
  }

  const handlePageDoubleClick = (pageId: string) => {
    // Set active page BEFORE switching mode to avoid activePageId-null pitfall
    useEditorStore.getState().setActivePage(pageId)
    useStudioStore.getState().setMode('page-composer')
  }

  const handleElementClick = (blockName: string, elementId: string, e: React.MouseEvent) => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      useEditorStore.getState().toggleSelection(elementId)
    } else {
      useEditorStore.getState().select(elementId, blockName)
    }
  }

  const handleAddPage = () => {
    const existingIds = app.pages.map((p) => p.id)
    // Generate a sequential name: "new-page", "new-page-2", "new-page-3", ...
    let idx = 1
    let pageId = 'new-page'
    while (existingIds.includes(pageId)) {
      idx++
      pageId = `new-page-${idx}`
    }
    useEditorStore.getState().addPage(pageId)
    useEditorStore.getState().selectPage(pageId)
  }

  const handleDuplicatePage = (pageId: string) => {
    useEditorStore.getState().duplicatePage(pageId)
    const nextPageId = useEditorStore.getState().activePageId
    if (nextPageId) useEditorStore.getState().selectPage(nextPageId)
  }

  const handleDeletePage = (pageId: string) => {
    useEditorStore.getState().removePage(pageId)
    const nextPageId = useEditorStore.getState().activePageId
    if (nextPageId) useEditorStore.getState().selectPage(nextPageId)
  }

  return (
    <div className="flex h-full flex-col bg-background" data-testid="layer-tree">
      {/* Summary bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b">
        <p className="text-xs text-muted-foreground">
          {copy.summary(app.pages.length, totalBlocks)}
        </p>
        <div className="flex items-center gap-0.5">
          {/* Expand all */}
          <button
            onClick={expandAll}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors',
              'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            title={copy.expandAll}
          >
            <UnfoldVertical className="h-3.5 w-3.5" />
          </button>
          {/* Collapse all */}
          <button
            onClick={collapseAll}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors',
              'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            title={copy.collapseAll}
          >
            <ChevronsDownUp className="h-3.5 w-3.5" />
          </button>
          {/* Search toggle */}
          <button
            onClick={() => setActiveRail('search')}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors',
              'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            title={copy.search}
          >
            <Search className="h-3.5 w-3.5" />
          </button>
          {/* Add page */}
          <button
            onClick={handleAddPage}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors',
              'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            title={copy.addPage}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* All pages — collapsible with blocks and element tree */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {app.pages.map((page) => {
            const isActivePage = activePageId === page.id
            return (
              <Collapsible
                key={page.id}
                open={openPages.has(page.id)}
                onOpenChange={(isOpen) => togglePage(page.id, isOpen)}
              >
                {/* Page header — double-click enters page-composer mode */}
                <div
                  className="group/page flex items-center"
                  onDoubleClick={() => handlePageDoubleClick(page.id)}
                >
                  <CollapsibleTrigger
                    onClick={() => handlePageClick(page.id)}
                    className={cn(
                      'flex flex-1 items-center gap-1 rounded-xl px-2 py-1.5 text-xs font-medium transition-colors [&[data-state=open]>svg.chevron]:rotate-90',
                      isActivePage
                        ? 'text-foreground bg-accent/30'
                        : 'text-muted-foreground hover:bg-accent/50',
                    )}
                  >
                    <ChevronRight className="chevron h-3 w-3 shrink-0 transition-transform" />
                    <FileText className="h-3 w-3 shrink-0" />
                    {renamingPageId === page.id ? (
                      <input
                        ref={pageRenameRef}
                        value={pageRenameDraft}
                        onChange={(e) => setPageRenameDraft(e.target.value)}
                        onBlur={commitPageRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitPageRename()
                          if (e.key === 'Escape') setRenamingPageId(null)
                          e.stopPropagation()
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 bg-transparent text-xs outline-none border-b border-primary min-w-0"
                        autoFocus
                      />
                    ) : (
                      <span className="truncate">{page.id}</span>
                    )}
                    <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
                      {page.blocks?.length ?? 0}
                    </span>
                  </CollapsibleTrigger>

                  {/* Page context menu trigger */}
                  <PageContextMenu
                    pageId={page.id}
                    canDelete={app.pages.length > 1}
                    onRename={() => startPageRename(page.id)}
                    onDuplicate={() => handleDuplicatePage(page.id)}
                    onDelete={() => handleDeletePage(page.id)}
                  />
                </div>

                <CollapsibleContent>
                  {!page.blocks || page.blocks.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">{copy.noBlocks}</p>
                  ) : (
                    <SortableContext
                      items={page.blocks ?? []}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="ml-1 pb-1">
                        {(page.blocks ?? []).map((blockName, index) => {
                          const block = blocks[blockName]
                          if (!block) return null
                          return (
                            <SortableLayerBlock
                              key={blockName}
                              blockName={blockName}
                              pageId={page.id}
                              index={index}
                              label={block.meta?.name ?? blockName}
                              isBlockSelected={selectedBlockName === blockName && selectionTier === 'block'}
                              isContainsSelection={selectedBlockName === blockName && selectionTier !== 'block'}
                            >
                              {/* Element tree under each block */}
                              <div className="ml-2" key={collapseKey}>
                                <ElementTreeNode
                                  element={block.ui.root}
                                  blockName={blockName}
                                  selectedIds={selectedIds}
                                  depth={0}
                                  onSelect={handleElementClick}
                                  defaultExpanded={defaultExpanded}
                                />
                              </div>
                            </SortableLayerBlock>
                          )
                        })}
                      </div>
                    </SortableContext>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}
// ---------------------------------------------------------------------------
// PageContextMenu — dropdown with Rename / Duplicate / Delete
// ---------------------------------------------------------------------------

function PageContextMenu({
  pageId,
  canDelete,
  onRename,
  onDuplicate,
  onDelete,
}: {
  pageId: string
  canDelete: boolean
  onRename: () => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { locale } = useStudioLocale()
  const copy = getLibraryPanelCopy(locale)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded transition-colors',
          'opacity-0 group-hover/page:opacity-100',
          'text-muted-foreground hover:bg-accent hover:text-foreground',
          open && 'opacity-100',
        )}
        title={copy.pageOptions}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 top-7 z-50 min-w-[140px] rounded-xl border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95">
          <button
            onClick={() => {
              setOpen(false)
              onRename()
            }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <Pencil className="h-3 w-3" />
            {copy.rename}
          </button>
          <button
            onClick={() => {
              setOpen(false)
              onDuplicate()
            }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <Copy className="h-3 w-3" />
            {copy.duplicate}
          </button>
          {canDelete && (
            <button
              onClick={() => {
                setOpen(false)
                onDelete()
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3" />
              {copy.delete}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// SortableLayerBlock — a block entry with DnD reordering
// ---------------------------------------------------------------------------

function SortableLayerBlock({
  blockName,
  pageId,
  index,
  label,
  isBlockSelected,
  isContainsSelection,
  children,
}: {
  blockName: string
  pageId: string
  index: number
  label: string
  isBlockSelected: boolean
  isContainsSelection: boolean
  children: React.ReactNode
}) {
  const { locale } = useStudioLocale()
  const copy = getLibraryPanelCopy(locale)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: blockName,
    data: {
      type: 'block' as const,
      pageId,
      index,
      label,
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('mt-1', isDragging && 'z-50 opacity-50')}
      {...attributes}
    >
      <div className="group/layer-block relative flex items-center">
        {/* Drag handle */}
        <div
          className="shrink-0 cursor-grab active:cursor-grabbing p-0.5 opacity-0 group-hover/layer-block:opacity-100 transition-opacity"
          {...listeners}
          title={copy.dragToReorder}
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </div>

        {/* Block label — click to select */}
        <button
          onClick={() => useEditorStore.getState().selectBlock(blockName)}
          className={cn(
            'flex flex-1 items-center gap-1.5 px-2 py-1 text-xs font-medium transition-colors rounded',
            isBlockSelected
              ? 'text-white'
              : isContainsSelection
                ? 'text-foreground bg-accent/50'
                : 'text-muted-foreground hover:bg-accent/50',
          )}
          style={isBlockSelected ? { backgroundColor: 'hsl(var(--editor-selection))' } : undefined}
        >
          <Box className="h-3 w-3" />
          <span className="truncate">{label}</span>
        </button>
      </div>

      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Element type icon map — maps element types to lucide-react icons
// ---------------------------------------------------------------------------

const ELEMENT_ICON_MAP: Record<string, LucideIcon> = {
  container: Square,
  section: Columns2,
  row: Columns2,
  grid: Grid2x2,
  card: CreditCard,
  text: Type,
  heading: Heading,
  image: Image,
  icon: Star,
  badge: Tag,
  button: MousePointerClick,
  input: TextCursorInput,
  textarea: AlignLeft,
  checkbox: CheckSquare,
  select: ChevronsUpDown,
  list: List,
  table: Table,
  separator: Minus,
  'block-ref': Link2,
}

function getElementIcon(type: string): LucideIcon {
  return ELEMENT_ICON_MAP[type] ?? Square
}

// ---------------------------------------------------------------------------
// ElementTreeNode — recursive element node for navigation
// ---------------------------------------------------------------------------

function ElementTreeNode({
  element,
  blockName,
  selectedIds,
  depth,
  onSelect,
  defaultExpanded: defaultExpandedProp,
}: {
  element: CogoElement
  blockName: string
  selectedIds: string[]
  depth: number
  onSelect: (blockName: string, elementId: string, e: React.MouseEvent) => void
  defaultExpanded?: boolean | null
}) {
  const [expanded, setExpanded] = useState(
    defaultExpandedProp != null ? defaultExpandedProp : depth < 2,
  )
  const [renaming, setRenaming] = useState(false)
  const [renameDraft, setRenameDraft] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const nodeRef = useRef<HTMLDivElement>(null)
  const hasChildren = element.children && element.children.length > 0
  const isSelected = selectedIds.includes(element.id)

  // Auto-scroll into view when selected (e.g., from canvas click)
  useEffect(() => {
    if (isSelected && nodeRef.current) {
      nodeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [isSelected])

  // Auto-expand parent when a child is selected
  useEffect(() => {
    if (!expanded && hasChildren && element.children) {
      const hasSelectedChild = element.children.some((child) =>
        selectedIds.includes(child.id) || hasSelectedDescendant(child, selectedIds),
      )
      if (hasSelectedChild) setExpanded(true)
    }
  }, [selectedIds])

  // Use meaningful ID as label when available, fall back to type
  const isGenerated = /^el-[0-9a-f]{8}/.test(element.id)
  const displayLabel = isGenerated ? element.type : element.id
  const isHidden = element.visible === false || element.visible === 'false'

  // Start inline rename
  const startRenaming = useCallback(() => {
    setRenameDraft(element.id)
    setRenaming(true)
    requestAnimationFrame(() => renameInputRef.current?.select())
  }, [element.id])

  // Commit rename
  const commitRename = useCallback(() => {
    setRenaming(false)
    const trimmed = renameDraft.trim()
    if (trimmed && trimmed !== element.id) {
      useEditorStore.getState().renameElement(blockName, element.id, trimmed)
      // Re-select with the new ID
      useEditorStore.getState().select(trimmed, blockName)
    }
  }, [renameDraft, element.id, blockName])

  // Right-click context menu
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null)
  const ctxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ctxMenu) return
    const handleClick = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setCtxMenu(null)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [ctxMenu])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Select this element first
    useEditorStore.getState().select(element.id, blockName)
    setCtxMenu({ x: e.clientX, y: e.clientY })
  }, [element.id, blockName])

  return (
    <div>
      <div ref={nodeRef} className="group/tree-node relative flex items-center">
        <button
          onClick={(e) => onSelect(blockName, element.id, e)}
          onDoubleClick={(e) => {
            e.stopPropagation()
            startRenaming()
          }}
          onContextMenu={handleContextMenu}
          onMouseEnter={() => useEditorStore.getState().setHoveredElement(element.id)}
          onMouseLeave={() => useEditorStore.getState().setHoveredElement(null)}
          className={cn(
            'flex flex-1 items-center gap-1 rounded px-2 py-0.5 text-left text-xs transition-colors',
            isSelected
              ? 'text-white'
              : 'text-foreground/70 hover:bg-accent/50',
            !isSelected && isHidden && 'opacity-40',
          )}
          style={{
            paddingLeft: 8 + depth * 12,
            ...(isSelected ? { backgroundColor: 'hsl(var(--editor-selection))' } : {}),
          }}
        >
          {hasChildren && (
            <ChevronRight
              className={cn(
                'h-3 w-3 shrink-0 transition-transform cursor-pointer',
                expanded && 'rotate-90',
              )}
              onClick={(e) => {
                e.stopPropagation()
                setExpanded(!expanded)
              }}
            />
          )}
          {!hasChildren && <span className="w-3" />}
          {(() => {
            const Icon = getElementIcon(element.type)
            return <Icon className={cn('h-3 w-3 shrink-0', isSelected ? 'text-white/80' : 'text-muted-foreground/70')} />
          })()}
          {renaming ? (
            <input
              ref={renameInputRef}
              className="flex-1 min-w-0 bg-background text-foreground text-xs rounded px-1 py-0 border border-ring outline-none"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitRename() }
                if (e.key === 'Escape') { setRenaming(false) }
                e.stopPropagation()
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <span className={cn('truncate', isSelected ? 'text-white/90' : 'text-muted-foreground')}>{displayLabel}</span>
              {element.text && (
                <span className={cn('truncate text-[10px] max-w-[80px]', isSelected ? 'text-white/70' : 'text-muted-foreground/60')}>
                  &quot;{element.text}&quot;
                </span>
              )}
              {/* Show short ID only for auto-generated elements (meaningful IDs are already the label) */}
              {isGenerated && (
                <span className={cn('ml-auto text-[10px] font-mono', isSelected ? 'text-white/60' : 'text-muted-foreground/50')}>
                  {element.id.slice(3, 9)}
                </span>
              )}
            </>
          )}
        </button>
        {/* Visibility toggle — appears on hover */}
        <button
          type="button"
          className={cn(
            'absolute right-1 p-0.5 rounded transition-opacity',
            (element.visible === false || element.visible === 'false')
              ? 'opacity-100 text-muted-foreground/40'
              : 'opacity-0 group-hover/tree-node:opacity-100 text-muted-foreground/60 hover:text-foreground',
          )}
          onClick={(e) => {
            e.stopPropagation()
            const isVisible = element.visible !== false && element.visible !== 'false'
            useEditorStore.getState().updateElement(blockName, element.id, { visible: !isVisible })
          }}
          title={(element.visible !== false && element.visible !== 'false') ? 'Hide' : 'Show'}
        >
          {(element.visible !== false && element.visible !== 'false')
            ? <Eye className="h-3 w-3" />
            : <EyeOff className="h-3 w-3" />
          }
        </button>
      </div>

      {/* Element context menu (right-click) */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          className="fixed z-[100] min-w-[180px] rounded-xl border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
        >
          <button
            onClick={() => { setCtxMenu(null); startRenaming() }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <Pencil className="h-3 w-3" /> Rename
          </button>
          <button
            onClick={() => { setCtxMenu(null); useEditorStore.getState().copySelection(); toast.info('Copied') }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <span className="flex items-center gap-2"><Copy className="h-3 w-3" /> Copy</span>
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.copy}</span>
          </button>
          <button
            onClick={() => { setCtxMenu(null); useEditorStore.getState().cutSelection(); toast.info('Cut') }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <span className="flex items-center gap-2"><Scissors className="h-3 w-3" /> Cut</span>
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.cut}</span>
          </button>
          <button
            onClick={() => {
              setCtxMenu(null)
              const { clipboard } = useEditorStore.getState()
              if (clipboard.elements.length > 0) { useEditorStore.getState().pasteClipboard(); toast.info('Pasted') }
            }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <span className="flex items-center gap-2"><Clipboard className="h-3 w-3" /> Paste</span>
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.paste}</span>
          </button>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => { setCtxMenu(null); useEditorStore.getState().duplicateSelection(); toast.info('Duplicated') }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <span className="flex items-center gap-2"><Copy className="h-3 w-3" /> Duplicate</span>
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.duplicate}</span>
          </button>
          <button
            onClick={() => { setCtxMenu(null); useEditorStore.getState().wrapElement(blockName, element.id) }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <WrapText className="h-3 w-3" /> Wrap in Container
          </button>
          <div className="my-1 h-px bg-border" />
          {(() => {
            const block = useEditorStore.getState().blocks[blockName]
            const parentInfo = block ? findParentInTree(block.ui.root, element.id) : null
            const canMoveUp = parentInfo ? parentInfo.index > 0 : false
            const canMoveDown = parentInfo && parentInfo.parent.children
              ? parentInfo.index < parentInfo.parent.children.length - 1
              : false
            return (
              <>
                <button
                  onClick={() => {
                    setCtxMenu(null)
                    useEditorStore.getState().moveElement(blockName, element.id, 'up')
                  }}
                  disabled={!canMoveUp}
                  className={cn(
                    'flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs',
                    canMoveUp ? 'hover:bg-accent' : 'opacity-40 pointer-events-none',
                  )}
                >
                  <span className="flex items-center gap-2"><ArrowUp className="h-3 w-3" /> Move Up</span>
                  <span className="text-[10px] text-muted-foreground">{SHORTCUT.moveUp}</span>
                </button>
                <button
                  onClick={() => {
                    setCtxMenu(null)
                    useEditorStore.getState().moveElement(blockName, element.id, 'down')
                  }}
                  disabled={!canMoveDown}
                  className={cn(
                    'flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs',
                    canMoveDown ? 'hover:bg-accent' : 'opacity-40 pointer-events-none',
                  )}
                >
                  <span className="flex items-center gap-2"><ArrowDown className="h-3 w-3" /> Move Down</span>
                  <span className="text-[10px] text-muted-foreground">{SHORTCUT.moveDown}</span>
                </button>
              </>
            )
          })()}
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => {
              setCtxMenu(null)
              useEditorStore.getState().removeElement(blockName, element.id)
              useEditorStore.getState().clearSelection()
            }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10"
          >
            <span className="flex items-center gap-2"><Trash2 className="h-3 w-3" /> Delete</span>
            <span className="text-[10px] text-destructive/60">{SHORTCUT.delete}</span>
          </button>
        </div>
      )}

      {expanded && hasChildren && element.children!.map((child, index) => (
        <ElementTreeNode
          key={`${element.id}:${child.id}:${index}`}
          element={child}
          blockName={blockName}
          selectedIds={selectedIds}
          depth={depth + 1}
          onSelect={onSelect}
          defaultExpanded={defaultExpandedProp}
        />
      ))}
    </div>
  )
}

// Check if any descendant of an element is in the selection
function hasSelectedDescendant(element: CogoElement, selectedIds: string[]): boolean {
  if (!element.children) return false
  return element.children.some(
    (child) => selectedIds.includes(child.id) || hasSelectedDescendant(child, selectedIds),
  )
}
